import { useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";

export default function InvitationBanner() {
  const { pendingInvitations, acceptInvitation, declineInvitation } = useAuth();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDialogElement>(null);

  if (!pendingInvitations || pendingInvitations.length === 0) {
    return null;
  }

  const handleAccept = async (invitationId: string) => {
    setProcessingId(invitationId);
    setError(null);
    try {
      const success = await acceptInvitation(invitationId);
      if (!success) {
        setError("Failed to accept invitation. Please try again.");
      }
    } catch (err) {
      console.error("Error accepting invitation:", err);
      setError("An error occurred while accepting the invitation.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (invitationId: string) => {
    setProcessingId(invitationId);
    setError(null);
    try {
      const success = await declineInvitation(invitationId);
      if (!success) {
        setError("Failed to decline invitation. Please try again.");
      }
    } catch (err) {
      console.error("Error declining invitation:", err);
      setError("An error occurred while declining the invitation.");
    } finally {
      setProcessingId(null);
    }
  };

  const openModal = () => {
    if (modalRef.current) {
      modalRef.current.showModal();
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-blue-800 font-medium">
            Organization Invitations
          </h3>
          <p className="text-blue-700 text-sm">
            You have {pendingInvitations.length} pending invitation
            {pendingInvitations.length > 1 ? "s" : ""}.
          </p>
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={openModal}
            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
          >
            View All
          </button>
        </div>
      </div>

      {/* Modal for invitations */}
      <dialog
        ref={modalRef}
        className="modal rounded-lg shadow-lg p-0 w-full max-w-md"
      >
        <div className="bg-white p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Organization Invitations</h2>

          <div className="space-y-4 max-h-80 overflow-y-auto">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center mb-2">
                  {invitation.organizations?.logo_url ? (
                    <img
                      src={invitation.organizations.logo_url}
                      alt={invitation.organizations.name}
                      className="w-10 h-10 rounded-full mr-3 object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 flex items-center justify-center">
                      <span className="text-gray-500 font-medium">
                        {invitation.organizations?.name?.charAt(0) || "O"}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium">
                      {invitation.organizations?.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Role:{" "}
                      {invitation.role.charAt(0).toUpperCase() +
                        invitation.role.slice(1)}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 mt-2">
                  <button
                    onClick={() => handleDecline(invitation.id)}
                    disabled={processingId === invitation.id}
                    className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    {processingId === invitation.id
                      ? "Processing..."
                      : "Decline"}
                  </button>
                  <button
                    onClick={() => handleAccept(invitation.id)}
                    disabled={processingId === invitation.id}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
                  >
                    {processingId === invitation.id
                      ? "Processing..."
                      : "Accept"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
